/**
 * "Desagregar un paquete": convierte un paquete de facturación (`items.is_set` +
 * `item_sets`) en líneas independientes de la venta/cotización del intranet.
 *
 * Reglas (ver odd/tasks/paquetes-desagregar.md):
 *   - cada componente entra como una línea propia, con cantidad
 *     `item_set.quantity × unidades del paquete`
 *   - el precio SIEMPRE sale del catálogo de cada componente (el paquete no fija
 *     precios); el vendedor edita comisión y descuento después
 *   - comisión y descuento arrancan en 0
 *   - un componente sin descripción (SUNAT lo rechaza) o con cantidad inválida no
 *     genera línea: se reporta en `omitidos` para poder avisarlo
 *   - `diferenciaTotal` dice cuánto cambia el total contra el precio del paquete,
 *     que es lo que la UI avisa antes de agregar
 *   - sin la opción activada nada de esto se usa: el paquete sigue entrando como
 *     un único ítem
 *
 * Sin imports de Vue/store salvo los helpers puros: se verifica fuera del
 * navegador con `npm run test:pack-lines`.
 */
import { calcLineSubTotal, toAmount } from './lineTotals'
import { esItemSeleccionable, descripcionItem } from './itemsSeleccionables'

export const DEFAULT_UNIDADES = 1

const esPaquete = (paquete) =>
    !!paquete && (paquete.is_set === true || paquete.is_set === 1 || paquete.tipo === 'package')

const unidadesValidas = (value) => {
    const unidades = toAmount(value)
    return unidades > 0 ? unidades : null
}

const redondear = (value) => Number(toAmount(value).toFixed(2))

const vacio = () => ({ lineas: [], omitidos: [], totalComponentes: 0, totalPaquete: 0, diferenciaTotal: 0 })

/**
 * @param {object} paquete paquete con `componentes: [{ ...producto, quantity }]`
 * @param {object} [options]
 * @param {number|string} [options.unidades] unidades del paquete que se están vendiendo
 * @param {number} [options.timestamp] base para que cada línea tenga su orden
 * @returns {{ lineas: Array, omitidos: Array, totalComponentes: number, totalPaquete: number, diferenciaTotal: number }}
 */
export const buildPackLines = (paquete, { unidades, timestamp = Date.now() } = {}) => {
    if (!esPaquete(paquete)) return vacio()

    const componentes = Array.isArray(paquete.componentes) ? paquete.componentes : []
    const unidadesPaquete = unidadesValidas(unidades) ?? DEFAULT_UNIDADES

    const lineas = []
    const omitidos = []

    componentes.forEach((componente) => {
        if (!componente || !componente.id) return

        const unidadesComponente = unidadesValidas(componente.quantity)

        if (!esItemSeleccionable(componente)) {
            omitidos.push({ id: componente.id, description: '', motivo: 'sin descripción' })
            return
        }

        if (unidadesComponente === null) {
            omitidos.push({
                id: componente.id,
                description: descripcionItem(componente),
                motivo: 'cantidad inválida'
            })
            return
        }

        const cantidad = unidadesComponente * unidadesPaquete
        const price = toAmount(componente.sale_unit_price)
        const { subTotal } = calcLineSubTotal({ price, comision: 0, unidades: cantidad, descuentos: 0 })
        const nombre = descripcionItem(componente)

        lineas.push({
            id: componente.id,
            name: componente.name,
            description: nombre,
            internal_id: componente.internal_id,
            unit_type_id: componente.unit_type_id,
            marca: componente.marca,
            sale_unit_price: componente.sale_unit_price,
            price,
            priceOrigen: componente.sale_unit_price,
            comision: 0,
            descuentos: 0,
            unidades: cantidad,
            idTipoMoneda: componente.idTipoMoneda || (componente.currency_type_id === 'USD' ? 2 : 1),
            rename: `${nombre} -|- ${componente.internal_id}`,
            supplier_name: componente.supplier_name || null,
            supplier_id: componente.supplier_id || null,
            subTotal,
            paquete_id: paquete.id,
            timestamp: timestamp + lineas.length
        })
    })

    const totalComponentes = redondear(lineas.reduce((acc, linea) => acc + toAmount(linea.subTotal), 0))
    const totalPaquete = redondear(toAmount(paquete.sale_unit_price) * unidadesPaquete)

    return {
        lineas,
        omitidos,
        totalComponentes,
        totalPaquete,
        diferenciaTotal: redondear(totalComponentes - totalPaquete)
    }
}

/**
 * Agrega las líneas del paquete al final de la lista actual sin tocar las líneas
 * ya cargadas.
 */
export const appendPackLines = (lista, resultado) => {
    const actuales = Array.isArray(lista) ? lista : []
    const nuevas = resultado && Array.isArray(resultado.lineas) ? resultado.lineas : []
    return actuales.concat(nuevas)
}
